<!-- HIGHER OR LOWER — two things, one tap, and three lives.

     Survival: it runs until three wrong. Eight seconds a question, and
     running out counts as wrong, so there is no waiting it out. -->
<template>
  <IonPage>
    <AppHeader title="Higher or Lower" back="/tabs/play" />

    <IonContent :fullscreen="true">
      <template v-if="!started">
        <div class="pad" style="padding-top: var(--sp-6)">
          <p class="ex-display lg">Higher or Lower</p>
          <p class="ex-sub">
            Two things. Tap the bigger, faster, older or heavier one. Eight
            seconds each, and three lives before you drop.
          </p>
        </div>
        <ModeRecord mode="higher-lower" />

        <div class="pad" style="margin-top: var(--sp-5)">
          <IonButton expand="block" size="large" @click="begin">Start comparing</IonButton>
        </div>
      </template>

      <template v-else-if="current">
        <GameHud :score="score" :streak="streak" label="Left" :value="seconds" :lives="lives" />
        <div v-if="timed" class="clock-bar" :class="clockClass" aria-hidden="true"><i :style="{ width: clockPct + '%' }" /></div>

        <div class="q-stage" ref="stageRef" tabindex="-1">
          <span class="q-topic">
            <span aria-hidden="true">{{ topicEmoji(current.topic) }}</span>
            {{ topicLabel(current.topic) }}
          </span>

          <p class="q-prompt">{{ current.question }}</p>

          <div class="answers">
            <button
              v-for="side in ['a', 'b']"
              :key="side"
              type="button"
              class="answer big"
              :class="sideClass(side)"
              :disabled="revealed"
              @click="answer(side)"
            >
              <span aria-hidden="true" style="font-size: 1.6rem">{{ current[side].emoji }}</span>
              <span>
                {{ current[side].label }}
                <em v-if="revealed" style="display:block; font-style:normal; opacity:.85; font-weight:700">
                  {{ current[side].display }}
                </em>
              </span>
              <span v-if="revealed && sideClass(side) === 'right'" class="sr-only"> — correct answer</span>
              <span v-else-if="revealed && sideClass(side) === 'wrong'" class="sr-only"> — your answer, incorrect</span>
            </button>
          </div>
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
    <AusBonus :when="ausNow" @done="ausNow = false" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import ModeRecord from '../components/ModeRecord.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import AusBonus from '../components/AusBonus.vue';
import { topicEmoji, topicLabel } from '../game/topics.js';
import { drawQuestions, pointsFor, isAusAnswer } from '../game/engine.js';
import { drawOptions } from '../game/profile.js';
import { useCountdown } from '../game/clock.js';
import { paced, timed } from '../game/pace.js';
import { regainLife } from '../game/lives.js';
import { finishRun } from '../game/run.js';
import { useStopOnLeave, useResetOnEnter } from '../game/guard.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';

const router = useRouter();
const PER_QUESTION_MS = 8000;

const started = ref(false);
const queue = ref([]);
const idx = ref(0);
const revealed = ref(false);
const chosen = ref('');
const ausNow = ref(false);
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const bestChain = ref(0);
const lives = ref(3);
const correct = ref(0);
const wrong = ref(0);
const ausCount = ref(0);
const verdict = ref({ text: '', why: '', good: true });

const clock = useCountdown(paced(PER_QUESTION_MS), () => timeUp());
const current = computed(() => queue.value[idx.value] ?? null);
const seconds = computed(() => Math.ceil(clock.remaining.value / 1000));
const clockPct = computed(() => (clock.remaining.value / paced(PER_QUESTION_MS)) * 100);
const clockClass = computed(() => (seconds.value <= 3 ? 'panic' : seconds.value <= 5 ? 'warn' : ''));

// Which side is correct is derived from the numbers, never stored — so a
// question cannot be added with its comparison silently inverted.
const winner = computed(() => (current.value && current.value.a.value >= current.value.b.value ? 'a' : 'b'));

let advanceTimer = null;
let ended = false;

function begin() {
  queue.value = drawQuestions('higher-lower', { ...drawOptions.value, count: 60 });
  started.value = true;
  clock.reset(paced(PER_QUESTION_MS));
  if (timed.value) clock.start();
  // The Start button is gone the instant this runs, so without this a
  // keyboard or screen-reader user is dropped back to <body>.
  nextTick(() => stageRef.value?.focus());
}

function sideClass(side) {
  if (!revealed.value) return '';
  if (side === winner.value) return 'right';
  if (side === chosen.value) return 'wrong';
  return 'muted';
}

function answer(side) {
  if (revealed.value || !current.value || ended) return;
  clock.stop();
  const q = current.value;
  revealed.value = true;
  chosen.value = side;
  const right = side === winner.value;

  if (right) {
    const aus = isAusAnswer(q, q[winner.value].label);
    if (aus) { ausCount.value += 1; ausNow.value = true; }
    score.value += pointsFor({ difficulty: q.difficulty, streak: streak.value, aus });
    streak.value += 1;
    regainLife(lives);
    bestChain.value = Math.max(bestChain.value, streak.value);
    correct.value += 1;
    haptic('success');
    verdict.value = { text: streak.value >= 3 ? pun('streak') : pun('correct'), why: q.reason, good: true };
    clearTimeout(advanceTimer);
    advanceTimer = setTimeout(next, 1400);
  } else {
    loseALife(q.reason, pun('wrong'));
  }
}

function timeUp() {
  if (revealed.value || ended) return;
  revealed.value = true;
  chosen.value = '';
  loseALife(current.value?.reason ?? '', pun('clock'), 'warning');
}

// Three lives rather than sudden death. The life is spent as the verdict
// appears, so the heart greying out is part of the same beat as being told
// the answer was wrong.
function loseALife(why, text, feel = 'error') {
  wrong.value += 1;
  streak.value = 0;
  lives.value -= 1;
  haptic(feel);
  verdict.value = { text, why, good: false };
  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(() => (lives.value <= 0 ? end() : next()), 1900);
}

function next() {
  verdict.value = { text: '', why: '', good: true };
  revealed.value = false;
  chosen.value = '';
  idx.value += 1;
  if (idx.value >= queue.value.length) {
    queue.value = [...queue.value, ...drawQuestions('higher-lower', { ...drawOptions.value, count: 40 })];
  }
  clock.reset(paced(PER_QUESTION_MS));
  if (timed.value) clock.start();
  // The question text changes silently under a screen reader otherwise — the
  // stage keeps focus for the whole round, but its content just swapped.
  const q = current.value;
  if (q) announce(`${q.question} ${q.a.label}, or ${q.b.label}?`);
}

async function end() {
  if (ended) return;
  ended = true;
  clock.stop();
  clearTimeout(advanceTimer);
  await finishRun({
    mode: 'higher-lower',
    score: score.value,
    lastedMs: 0,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: ausCount.value,
  });
  router.replace('/tabs/play/results');
}

// Everything this screen has running, stopped. Idempotent: it is called on
// every leave, on abandon, and again before every re-entry.
function teardown() {
  ended = true;
  clock.stop();
  clearTimeout(advanceTimer);
}

// Back to the pre-game screen. Without this, "Go again" returned to this same
// cached instance still holding the finished run.
function resetRun() {
  teardown();
  ended = false;
  // Every ref back to the literal it was declared with — the state a
  // fresh mount would have. Resetting only the run flags left the LAST
  // question's verdict and its revealed answer painted over the new
  // board, so a new game opened showing why the previous one ended.
  started.value = false;
  queue.value = [];
  idx.value = 0;
  revealed.value = false;
  chosen.value = '';
  ausNow.value = false;
  stageRef.value = null;
  score.value = 0;
  streak.value = 0;
  bestChain.value = 0;
  lives.value = 3;
  correct.value = 0;
  wrong.value = 0;
  ausCount.value = 0;
  verdict.value = { text: '', why: '', good: true };
}

useStopOnLeave(teardown);
useResetOnEnter(resetRun);

onUnmounted(() => { ended = true; clearTimeout(advanceTimer); });
</script>
